/**
 * SentAttachments — renders the attachment strip inside a sent user message bubble.
 *
 * Images render as thumbnail buttons that open a lightbox on click.
 * All other kinds (PDF, audio, URL, code, text) render as icon+name chips.
 *
 * Handles missing thumbnails gracefully: if `thumbnailDataUrl` is absent
 * (older restored session) or the data URL fails to load, the image falls
 * back to the same file-chip style as other attachment types.
 */
import { memo, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FileText, Code2, Link2, Mic, File, ImageIcon, X } from "lucide-react";
import type { ChatMsgAttachment } from "@/stores/multiAgentStore";

// ── Icon & label helpers ──────────────────────────────────────────────────────

function KindIcon({ kind }: { kind: string }) {
  if (kind === "pdf")   return <FileText  className="h-3.5 w-3.5 shrink-0" />;
  if (kind === "code")  return <Code2     className="h-3.5 w-3.5 shrink-0" />;
  if (kind === "url")   return <Link2     className="h-3.5 w-3.5 shrink-0" />;
  if (kind === "audio") return <Mic       className="h-3.5 w-3.5 shrink-0" />;
  if (kind === "image") return <ImageIcon className="h-3.5 w-3.5 shrink-0" />;
  return                       <File      className="h-3.5 w-3.5 shrink-0" />;
}

const KIND_LABEL: Record<string, string> = {
  pdf: "PDF", code: "Code", url: "Link", audio: "Audio", image: "Image", text: "Text",
};

// ── Lightbox ──────────────────────────────────────────────────────────────────

interface LightboxProps { src: string; alt: string; onClose: () => void; }

function ImageLightbox({ src, alt, onClose }: LightboxProps) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal
      aria-label={`Preview: ${alt}`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9990,
        background: "rgba(0,0,0,0.82)",
        backdropFilter: "blur(6px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
      }}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          width: 32,
          height: 32,
          borderRadius: 999,
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <X className="h-4 w-4" />
      </button>

      {/* Image — stop propagation so clicking the image doesn't close */}
      <img
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "min(88vw, 1200px)",
          maxHeight: "80vh",
          objectFit: "contain",
          borderRadius: 8,
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        }}
      />

      {/* Filename caption */}
      <p
        style={{
          color: "rgba(255,255,255,0.55)",
          fontSize: 12,
          textAlign: "center",
          pointerEvents: "none",
          maxWidth: "80vw",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {alt}
      </p>
    </div>,
    document.body,
  );
}

// ── Image thumbnail ───────────────────────────────────────────────────────────

function ImageAttachment({ attachment }: { attachment: ChatMsgAttachment }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const [hovered, setHovered] = useState(false);

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const handleError = useCallback(() => setBroken(true), []);

  // Fall back to file chip if no thumbnail or image failed to decode
  if (!attachment.thumbnailDataUrl || broken) {
    return <FileChip attachment={attachment} />;
  }

  return (
    <>
      <button
        type="button"
        onClick={openLightbox}
        title={`Click to preview — ${attachment.name}`}
        aria-label={`Preview image: ${attachment.name}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: 0,
          border: `1px solid color-mix(in oklab, hsl(var(--primary)) 22%, var(--ma-border))`,
          borderRadius: 8,
          overflow: "hidden",
          cursor: "zoom-in",
          background: "none",
          display: "block",
          flexShrink: 0,
          transition: "box-shadow 0.15s, transform 0.15s",
          boxShadow: hovered ? "var(--ma-shadow-md)" : "none",
          transform: hovered ? "scale(1.03)" : "scale(1)",
        }}
      >
        <img
          src={attachment.thumbnailDataUrl}
          alt={attachment.name}
          onError={handleError}
          loading="lazy"
          decoding="async"
          style={{
            display: "block",
            height: 90,
            width: "auto",
            maxWidth: 200,
            objectFit: "cover",
          }}
        />
      </button>

      {lightboxOpen && (
        <ImageLightbox
          src={attachment.thumbnailDataUrl}
          alt={attachment.name}
          onClose={closeLightbox}
        />
      )}
    </>
  );
}

// ── File chip (non-image attachments + image fallback) ────────────────────────

function FileChip({ attachment }: { attachment: ChatMsgAttachment }) {
  const [hovered, setHovered] = useState(false);
  const label = KIND_LABEL[attachment.kind] ?? "File";

  return (
    <div
      role="img"
      aria-label={`${label}: ${attachment.name}`}
      title={attachment.name}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        maxWidth: 220,
        padding: "5px 10px",
        borderRadius: 8,
        fontSize: "var(--ma-fs-caption, 12px)",
        background: hovered
          ? "color-mix(in oklab, hsl(var(--primary)) 14%, hsl(var(--card)))"
          : "color-mix(in oklab, hsl(var(--primary)) 8%, hsl(var(--card)))",
        border: "1px solid color-mix(in oklab, hsl(var(--primary)) 22%, var(--ma-border))",
        color: "var(--ma-text)",
        transition: "background 0.12s",
        userSelect: "none",
      }}
    >
      <span style={{ color: "var(--ma-text-muted)" }} aria-hidden>
        <KindIcon kind={attachment.kind} />
      </span>

      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
        }}
      >
        {attachment.name}
      </span>

      <span
        style={{
          fontSize: 10,
          color: "var(--ma-text-muted)",
          flexShrink: 0,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 600,
        }}
        aria-hidden
      >
        {label}
      </span>
    </div>
  );
}

// ── Public component ──────────────────────────────────────────────────────────

/**
 * Renders the attachment strip inside a sent user message bubble.
 * Memoized — thread turns are immutable once committed, so this avoids
 * re-rendering every turn on every streaming token in ChatPanel.
 */
export const SentAttachments = memo(function SentAttachments({
  attachments,
}: {
  attachments?: ChatMsgAttachment[];
}) {
  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => a.kind === "image");
  const files  = attachments.filter((a) => a.kind !== "image");

  return (
    <div style={{ marginBottom: 8 }}>
      {images.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            marginBottom: files.length > 0 ? 6 : 0,
          }}
        >
          {images.map((a) => (
            <ImageAttachment key={a.id} attachment={a} />
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {files.map((a) => (
            <FileChip key={a.id} attachment={a} />
          ))}
        </div>
      )}
    </div>
  );
});
